import { useMutation, useQueries, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import {
  API_ENDPOINTS,
  CONTRACT_DOC_TYPE,
  DELIVERABLE_FRAMEWORK_TYPE,
} from "../../../helpers/constants";
import apiInstance from "../../instances/apiInstance";

import { AxiosResponse } from "axios";
import { UserDetails } from "../../../helpers/types/common";
import {
  getValueOrBlank,
  isEmpty,
  validateApiParamValue,
} from "../../../helpers/utils";
import {
  PurchaseOrder,
  PurchaseOrderExpensePayload,
} from "../../../pages/createClient/modelsAndSchemas/Models";
import {
  InvoicingLanguage,
  ProgramInvoiceSummary,
  ViewInvoiceResponse,
} from "../../../pages/createProgram/modelsAndSchemas/Models";
import {
  DropDownData,
  IdNameList,
  InvoiceHistoryRowExportParams,
  ReverseInvoiceParams,
} from "../common/models";
import { ViewFeeOrExpenseDetails } from "../program/models";
import {
  AddExistingDeliverableParams,
  AddNewContactParams,
  AddPurchaseOrderParams,
  ArchiveStatusOtherDocumentParams,
  BillingDetailsAndConfigurationResponse,
  ClientSearchParams,
  ContactListParams,
  CreateClientActivityPayload,
  CreateClientParams,
  CreateClientResponse,
  CreateDeliverableParams,
  CreateGenericAccountParams,
  DeleteClientSpecificContentParams,
  DeleteContactParams,
  DeleteDocumentParams,
  DeletePurchaseOrderParams,
  DocumentUpdateParams,
  DocumentUploadParams,
  ExistingDeliverableParams,
  FeatureListParams,
  FileDownloadParams,
  FileUploadParams,
  FileUploadResponse,
  GenericAccountKeyDetails,
  GenericAccountParticipantResponse,
  GetClientDetailsResponse,
  GetClientListParams,
  GetClientListResponse,
  GetClientSalesOfficesListResponse,
  GetContactParams,
  GetContractDocumentPayload,
  GetContractDocumentResponse,
  GetCountriesByGeoScopeParams,
  GetDocumentListParams,
  GetGenericAccountsResponse,
  GetInvoicingLanguagesParams,
  GetPurchaseOrderParams,
  GetPurchaseOrderResponse,
  GetUpdateContactModel,
  InheritDocumentsPayload,
  InvoiceDownloadParams,
  POAdjustmentParams,
  ReopenPurchaseOrderParams,
  ResourcesAndProgramTemplatesParams,
  SalesOfficeByLocalClientRelationshipOwnerIdResponse,
  SaveSearchFilterParams,
  SearchDocumentResponse,
  SearchGenericAccountsParams,
  SearchInheritDocumentsPayload,
  SearchPageName,
  TagRemoveParentClientParams,
  UnmapContractDocumentsPayload,
  UnmapOtherDocumentsPayload,
  UpdateClientParams,
  UpdateGenericAccountParams,
  UpdateParticipantDataParams,
  UpdatePurchaseOrderParams,
  UpdateResourcesAndProgramTemplatesParams,
  UpdateStatusContractDocumentParams,
  UpdateStatusOtherDocumentParams,
  ValidateOpportunityPayload,
} from "./models";

export const useSearchClientListMutation = () => {
  return useMutation({
    mutationFn: (params: ClientSearchParams) => {
      return apiInstance.post(
        `${API_ENDPOINTS.CLIENT_BASE_URL}${API_ENDPOINTS.GET_CLIENT_LIST}`,
        params,
      );
    },
  });
};

export const useSearchClientSaveFilterMutation = () => {
  return useMutation({
    mutationFn: (params: SaveSearchFilterParams) => {
      return apiInstance.post(API_ENDPOINTS.POST_SAVE_FILTER, params);
    },
  });
};

export const useCreateClientMutation = () => {
  return useMutation<CreateClientResponse, Error, CreateClientParams>({
    mutationFn: (params: CreateClientParams) => {
      return apiInstance.post(API_ENDPOINTS.POST_CLIENT, params);
    },
  });
};

export const useGetClientByIdMutation = () => {
  return useMutation({
    mutationFn: (
      clientId: string,
    ): Promise<{ data: GetClientDetailsResponse }> => {
      return apiInstance.get(`${API_ENDPOINTS.getClientById(clientId)}`);
    },
  });
};

export const useGetBillingDetailsAndConfigByClientIdMutation = () => {
  return useMutation({
    mutationFn: (
      clientId: string,
    ): Promise<{ data: BillingDetailsAndConfigurationResponse }> => {
      return apiInstance.get(
        `${API_ENDPOINTS.getBillingDetailsAndConfigByClientId(clientId)}`,
      );
    },
  });
};

export const useUpdateClientDetailsMutation = () => {
  return useMutation({
    mutationFn: (params: UpdateClientParams) => {
      return apiInstance.put(
        `${API_ENDPOINTS.updateClientDetailsById(params.clientId)}`,
        params,
      );
    },
  });
};

// while removing parent client, we need to pass parentClientId: null instead of ID & vice versa for tag parent-client.
export const useTagParentClientMutation = () => {
  return useMutation({
    mutationFn: (params: TagRemoveParentClientParams) => {
      return apiInstance.patch(
        `${API_ENDPOINTS.tagRemoveParentClient(params.clientId)}`,
        params,
      );
    },
  });
};

// api for getting details for staff list
export const useGetStaffListByJobRoleOrClientId = ({
  jobRole,
  clientId,
}: {
  jobRole?: "localClientRelationshipOwner" | "dedicatedProjectManager" | null;
  clientId?: string;
}) => {
  return useQuery({
    queryKey: ["getStaffList", `${clientId || jobRole}`],
    queryFn: async (): Promise<UserDetails[]> => {
      let url = API_ENDPOINTS.GET_STAFF_LIST_USERS;
      const queryParams = new URLSearchParams();
      if (clientId) {
        queryParams.append("clientId", clientId);
        url = `${API_ENDPOINTS.GET_STAFF_LIST_USERS}?${queryParams.toString()}`;
      } else if (jobRole) {
        queryParams.append("jobFunction", jobRole);
        url = `${API_ENDPOINTS.GET_STAFF_LIST_USERS}?${queryParams.toString()}`;
      }
      const res = await apiInstance.get(url);
      return res.data;
    },
  });
};

export const useGetHrAndBillingContactsMutation = () => {
  return useMutation({
    mutationFn: (params: ContactListParams): Promise<UserDetails[]> => {
      return apiInstance
        .get(
          `${API_ENDPOINTS.getHrBillingContactList(params.clientId)}?contactType=${params.contactType}`,
        )
        .then((res) => res.data);
    },
  });
};

// api for getting List of saved filters on client search page
export const useSearchClientSavedFiltersMutation = ({
  searchPageName,
}: {
  searchPageName: SearchPageName;
}) => {
  return useQuery({
    queryKey: ["getPageName", `${searchPageName}`],
    queryFn: async () => {
      const res = await apiInstance.get(
        `${API_ENDPOINTS.GET_SAVED_FILTERS}?searchpagename=${searchPageName}`,
      );
      return res.data;
    },
  });
};

export const useDeleteClientSavedFiltersMutation = () => {
  return useMutation<null, Error, any>({
    mutationFn: (params: []) => {
      return apiInstance.delete(API_ENDPOINTS.DELETE_SAVED_FILTERS, {
        data: params,
      });
    },
  });
};

// api for getting contacts list of client on create client page
export const useClientAvailableContactsMutation = () => {
  return useMutation({
    mutationFn: (clientId: string) => {
      return apiInstance.get(
        `${API_ENDPOINTS.getClientAvailableContact(clientId)}`,
      );
    },
  });
};

export const useAddNewContactMutation = () => {
  return useMutation({
    mutationFn: (params: AddNewContactParams) => {
      return apiInstance
        .post(`${API_ENDPOINTS.addNewContact(params.clientId)}`, params)
        .then((res) => {
          return res.data;
        });
    },
  });
};

export const useGetClientContactByIdMutation = () => {
  return useMutation({
    mutationFn: (params: GetContactParams) => {
      return apiInstance.get(
        `${API_ENDPOINTS.getUpdateClientContactById(params.clientId, params.contactId)}`,
      );
    },
  });
};

export const useUpdateContactMutation = () => {
  return useMutation({
    mutationFn: (params: GetUpdateContactModel) => {
      return apiInstance.put(
        `${API_ENDPOINTS.getUpdateClientContactById(params.clientId, params.contactId)}`,
        params,
      );
    },
  });
};

export const useDeleteContactMutation = () => {
  return useMutation({
    mutationFn: (params: DeleteContactParams) => {
      return apiInstance.delete(
        `${API_ENDPOINTS.deleteContact(params.clientId, params.contactId)}`,
      );
    },
  });
};

export const useGetClientHierarchyById = () => {
  return useMutation({
    mutationFn: (clientId: string) => {
      return apiInstance.get(
        `${API_ENDPOINTS.getClientHierarchyById(clientId)}`,
      );
    },
  });
};

export const useGetCountryListForDropdowns = () => {
  return useQuery({
    queryKey: ["getCountryList"],
    queryFn: () => {
      return apiInstance
        .get(API_ENDPOINTS.GET_COUNTRY_DROPDOWN_LIST)
        .then((res) => {
          return res.data[0].items;
        });
    },
    retry: false,
  });
};

export const useAddClientSpecificContentResourcesAndProgramTemplates = () => {
  return useMutation({
    mutationFn: (params: ResourcesAndProgramTemplatesParams) => {
      return apiInstance.post(
        API_ENDPOINTS.addClientSpecificContentResourcesAndProgramTemplates(
          params.clientId,
        ),
        params,
      );
    },
  });
};

// Need to change the API URL & check params once API is ready.
export const useUpdateClientSpecificContentMutation = () => {
  return useMutation({
    mutationFn: (params: UpdateResourcesAndProgramTemplatesParams) => {
      return apiInstance.put(
        API_ENDPOINTS.updateClientSpecificContent(params.groupId),
        params,
      );
    },
  });
};

// api for getting client specific content list of client on create client page
export const useClientSpecificContentMutation = () => {
  return useMutation({
    mutationFn: (clientId: string) => {
      return apiInstance.get(
        `${API_ENDPOINTS.getClientSpecificContent(clientId)}`,
      );
    },
  });
};

export const useDeleteClientSpecificContentMutation = () => {
  return useMutation({
    mutationFn: (params: DeleteClientSpecificContentParams) => {
      return apiInstance.delete(
        `${API_ENDPOINTS.deleteClientSpecificContent(params.clientResourceGroupId)}`,
      );
    },
  });
};

export const useGetDocumentListByClientId = () => {
  return useMutation({
    mutationFn: (params: GetDocumentListParams) => {
      return apiInstance.get(
        `${API_ENDPOINTS.getDocumentListByClientId(params.clientId)}?documentType=${params.documentType}`,
      );
    },
  });
};

export const useArchiveDocumentListByClientId = () => {
  return useMutation({
    mutationFn: (params: { clientId: string }) => {
      return apiInstance.get(
        `${API_ENDPOINTS.getArchiveDocumentListByClientId(params.clientId)}`,
      );
    },
  });
};

export const useDeleteContractDocumentMutation = () => {
  return useMutation({
    mutationFn: (params: DeleteDocumentParams) => {
      return apiInstance.delete(
        `${API_ENDPOINTS.deleteContractDocument(params.documentId)}`,
      );
    },
  });
};

export const useUpdateStatusContractDocumentMutation = () => {
  return useMutation({
    mutationFn: (params: UpdateStatusContractDocumentParams) => {
      return apiInstance.post(
        `${API_ENDPOINTS.updateStatusContractDocument(params.documentId)}`,
        params,
      );
    },
  });
};

// Temp code, need to change once API is ready.
export const useDeleteOtherDocumentMutation = () => {
  return useMutation({
    mutationFn: (params: DeleteDocumentParams) => {
      return apiInstance.delete(
        `${API_ENDPOINTS.deleteOtherDocument(params.documentId)}`,
      );
    },
  });
};

export const useUpdateStatusOtherDocumentMutation = () => {
  return useMutation({
    mutationFn: (params: UpdateStatusOtherDocumentParams) => {
      return apiInstance.post(
        `${API_ENDPOINTS.updateStatusOtherDocument(params.documentId)}`,
        params,
      );
    },
  });
};

export const useArchiveStatusOtherDocumentMutation = () => {
  return useMutation({
    mutationFn: (params: ArchiveStatusOtherDocumentParams) => {
      const { documentId, ...queryParams } = params;
      return apiInstance.post(
        `${API_ENDPOINTS.archiveStatusOtherDocument(params.documentId)}`,
        queryParams,
      );
    },
  });
};

export const useArchiveStatusContractDocumentMutation = () => {
  return useMutation({
    mutationFn: (params: ArchiveStatusOtherDocumentParams) => {
      const { documentId, ...queryParams } = params;
      return apiInstance.post(
        `${API_ENDPOINTS.archiveStatusContractDocument(params.documentId)}`,
        queryParams,
      );
    },
  });
};

export const useUnmapContractDocumentMutation = () => {
  return useMutation({
    mutationFn: (params: UnmapContractDocumentsPayload) => {
      return apiInstance.post(API_ENDPOINTS.unmapContractDocuments, params);
    },
  });
};

export const useUnmapOtherDocumentMutation = () => {
  return useMutation({
    mutationFn: (params: UnmapOtherDocumentsPayload) => {
      return apiInstance.post(API_ENDPOINTS.unmapOtherDocuments, params);
    },
  });
};

export const useGetResourceByGroupId = () => {
  return useMutation({
    mutationFn: async (groupId: string) => {
      const res = await apiInstance.get(
        `${API_ENDPOINTS.getResourceByGroupId(groupId)}`,
      );
      return res.data;
    },
  });
};

// api to get client list in create program screen
export const useGetClientListWithCode = () => {
  return useQuery({
    queryKey: ["getClientList"],
    queryFn: async () => {
      const res = await apiInstance.get(
        API_ENDPOINTS.GET_CLIENT_LIST_FOR_PROGRAM,
      );
      return res.data;
    },
    retry: false,
  });
};

export const useUploadDocumentDetailsMutation = () => {
  return useMutation({
    mutationFn: (
      params: DocumentUploadParams<"client_contract" | "client_others">,
    ) => {
      return apiInstance.post(
        params.documentType === "client_contract"
          ? `${API_ENDPOINTS.uploadContractDocumentsById(params.clientId)}`
          : `${API_ENDPOINTS.uploadOtherDocumentsById(params.clientId)}`,
        params.documentPayload,
      );
    },
  });
};

export const useSearchDocumentsMutation = () => {
  return useMutation({
    mutationFn: async (
      payload: SearchInheritDocumentsPayload,
    ): Promise<SearchDocumentResponse[]> => {
      const res = await apiInstance.post(
        API_ENDPOINTS.searchDocuments,
        payload,
      );
      return res.data;
    },
  });
};

export const useInheritDocumentsMutation = () => {
  return useMutation({
    mutationFn: async (payload: InheritDocumentsPayload) => {
      const res = await apiInstance.post(
        API_ENDPOINTS.inheritDocuments,
        payload,
      );
      return res.data;
    },
  });
};

export const useUpdateContractDocumentMutation = () => {
  return useMutation({
    mutationFn: (
      params: DocumentUpdateParams<"client_contract" | "client_others">,
    ) => {
      return apiInstance.put(
        params.documentType === "client_contract"
          ? `${API_ENDPOINTS.updateContractDocument(params.id)}`
          : `${API_ENDPOINTS.updateOtherDocument(params.id)}`,
        params.documentPayload,
      );
    },
  });
};

export const useCountryListByGeoScope = () => {
  return useMutation({
    mutationFn: (params: GetCountriesByGeoScopeParams) => {
      return apiInstance
        .get(API_ENDPOINTS.getCountriesByGeoScope(params.geoScopeId))
        .then((res) => {
          return res.data;
        });
    },
  });
};

export const useUploadFileMutation = () => {
  return useMutation({
    mutationFn: (params: FileUploadParams) => {
      const formData = new FormData();
      formData.append("documentType", params.documentType);
      formData.append("entityType", params.entityType);
      formData.append("entityId", getValueOrBlank(params.entityId));
      formData.append("files", params.file);
      formData.append("operationType", params.operationType);

      return apiInstance
        .post(API_ENDPOINTS.UPLOAD_DOCUMENTS, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        })
        .then((res) => {
          return res.data[0] as FileUploadResponse;
        });
    },
  });
};

export const useFileDownloadMutation = () => {
  return useMutation({
    mutationFn: (params: FileDownloadParams) => {
      return apiInstance
        .get(
          `${API_ENDPOINTS.getDocumentById(params.documentId)}?documentType=${params.documentType}`,
          {
            responseType: "blob",
            headers: {
              "Content-Type": "application/octet-stream",
            },
          },
        )
        .then((res) => {
          let contentTypeHeader = res.headers["content-type"];
          // to extract filename
          const contentDisposition = res.headers["content-disposition"];
          /**converting binary file to blob here */
          let blob = new Blob([res.data], {
            type: contentTypeHeader?.toString().toLowerCase(),
          });
          return {
            blob,
            contentDisposition,
          };
        });
    },
  });
};

export const useDownloadInvoiceToExcelMutation = () => {
  return useMutation<AxiosResponse<Blob>, Error, InvoiceDownloadParams>({
    mutationFn: (
      params: InvoiceDownloadParams,
    ): Promise<AxiosResponse<Blob>> => {
      let url = API_ENDPOINTS.downloadInvoiceByProgramId(params.invoiceId);
      if (
        params.deliverableFrameworkType === DELIVERABLE_FRAMEWORK_TYPE.PROJECT
      ) {
        url = API_ENDPOINTS.downloadInvoiceByProjectId(params.invoiceId);
      }
      return apiInstance.get(url, {
        responseType: "blob",
      });
    },
  });
};

export const useGetPurchaseOrders = () => {
  const [params, setParams] = useState<GetPurchaseOrderParams>();

  const query = useQuery({
    queryKey: ["getPurchaseOrders", params],
    queryFn: async (): Promise<PurchaseOrder[]> => {
      if (!params) {
        return [];
      }

      const queryParams = new URLSearchParams();
      const { statusId, clientCode } = params;
      if (statusId) {
        queryParams.append("statusId", validateApiParamValue(statusId));
      }
      let url = `${API_ENDPOINTS.getPurchaseOrder(clientCode)}`;
      const queryString = queryParams.toString();
      if (queryString) {
        url += `?${queryString}`;
      }
      const res = await apiInstance.get(url);

      return res.data;
    },
    enabled: !!params, // Only run when params are set
    retry: false,
  });

  const refetchWithParams = (newParams: GetPurchaseOrderParams) => {
    setParams(newParams);
  };

  return { ...query, refetchWithParams };
};

export const useGetPurchaseOrderByIdMutation = () => {
  return useMutation({
    mutationFn: async (
      purchaseOrderId: string,
    ): Promise<GetPurchaseOrderResponse> => {
      const res = await apiInstance.get(
        API_ENDPOINTS.getPurchaseOrderById(purchaseOrderId),
      );
      return res.data;
    },
  });
};

export const useAddPurchaseOrdersMutation = () => {
  return useMutation({
    mutationFn: (params: AddPurchaseOrderParams) => {
      return apiInstance.post(
        API_ENDPOINTS.addPurchaseOrder(params.clientId),
        params,
      );
    },
  });
};

export const useUpdatePurchaseOrdersMutation = () => {
  return useMutation({
    mutationFn: (params: UpdatePurchaseOrderParams) => {
      return apiInstance.put(
        API_ENDPOINTS.getPurchaseOrderById(params.purchaseOrderId),
        params,
      );
    },
  });
};

export const useReopenPurchaseOrdersMutation = () => {
  return useMutation({
    mutationFn: (params: ReopenPurchaseOrderParams) => {
      return apiInstance.post(
        API_ENDPOINTS.reOpenPurchaseOrderById(params.purchaseOrderId),
        params,
      );
    },
  });
};

export const useGetLocalClientRelationshipOwnerList = () => {
  const [params, setParams] = useState<string>();

  const query = useQuery({
    queryKey: ["getLocalClientRelationshipOwnerList", params],
    queryFn: async () => {
      if (!params) {
        return [];
      }
      const res = await apiInstance.get(
        API_ENDPOINTS.getLocalClientRelationshipOwnerList(params ?? ""),
      );

      return res.data;
    },
    enabled: !!params, // Only run when params are set
    retry: false,
  });

  const refetchWithParams = (newParams: string) => {
    setParams(newParams);
  };

  return { ...query, refetchWithParams };
};

export const usePostPOTransaction = () => {
  return useMutation({
    mutationFn: (params: POAdjustmentParams) => {
      return apiInstance.post(
        API_ENDPOINTS.postPurchaseOrderTransactions(params.purchaseOrderId),
        params,
      );
    },
  });
};

export const useGetPOTransactions = () => {
  return useMutation({
    mutationFn: async (purchaseOrderId: string) => {
      const res = await apiInstance.get(
        `${API_ENDPOINTS.getPurchaseOrderTransactions(purchaseOrderId)}`,
      );
      return res.data;
    },
  });
};

export const useGetContractClientDocument = () => {
  return useMutation({
    mutationFn: async (documentId: string) => {
      const res = await apiInstance.get(
        `${API_ENDPOINTS.getContractDocumentById(documentId)}`,
      );
      return res.data;
    },
  });
};

export const useGetOtherClientDocument = () => {
  return useMutation({
    mutationFn: async (documentId: string) => {
      const res = await apiInstance.get(
        `${API_ENDPOINTS.getOtherClientDocumentById(documentId)}`,
      );
      return res.data;
    },
  });
};

export const useGetArchiveClientDocument = () => {
  return useMutation({
    mutationFn: async ({
      documentId,
      documentType,
    }: {
      documentId: string;
      documentType: string;
    }) => {
      const url =
        documentType === CONTRACT_DOC_TYPE
          ? API_ENDPOINTS.getContractDocumentById(documentId)
          : API_ENDPOINTS.getOtherClientDocumentById(documentId);
      const res = await apiInstance.get(url);
      return res.data;
    },
  });
};

export const useRestoreArchiveClientDocument = () => {
  return useMutation({
    mutationFn: async ({
      documentId,
      documentType,
    }: {
      documentId: string;
      documentType: string;
    }) => {
      const url =
        documentType === CONTRACT_DOC_TYPE
          ? API_ENDPOINTS.restoreContractArchiveDocument(documentId)
          : API_ENDPOINTS.restoreOtherArchiveDocument(documentId);
      const res = await apiInstance.post(url);
      return res.data;
    },
  });
};

export const useGetSalesOfficeByLocalClientRelationshipOwnerId = () => {
  const [params, setParams] = useState<string>();

  const query = useQuery({
    queryKey: ["getSalesOfficeByLocalClientRelationshipOwnerId", params],
    queryFn:
      async (): Promise<SalesOfficeByLocalClientRelationshipOwnerIdResponse> => {
        if (!params) {
          return {} as SalesOfficeByLocalClientRelationshipOwnerIdResponse;
        }
        const res = await apiInstance.get(
          API_ENDPOINTS.getSalesOfficeByLocalClientRelationshipOwnerId(
            params ?? "",
          ),
        );

        return res.data;
      },
    enabled: !!params, // Only run when params are set
    retry: false,
  });
  const refetchWithParams = (newParams: string) => {
    setParams(newParams);
  };

  return { ...query, refetchWithParams };
};

export const useDeletePOMutation = () => {
  return useMutation({
    mutationFn: (params: DeletePurchaseOrderParams) => {
      return apiInstance.delete(
        `${API_ENDPOINTS.deletePurchaseOrder(params.purchaseOrderId)}`,
      );
    },
  });
};

export const useDeleteCreateClientDeliverableMutation = () => {
  return useMutation<null, Error, any>({
    mutationFn: ({ deliverableId }: { deliverableId: string }) => {
      return apiInstance.delete(
        `${API_ENDPOINTS.CLIENT_BASE_URL}/clientDeliverables/${validateApiParamValue(deliverableId)}`,
      );
    },
  });
};

export const useGetClientActivityById = (id: string) => {
  return useQuery({
    queryKey: ["getClientActivityById", id],
    queryFn: async () => {
      const res = await apiInstance.get(
        `${API_ENDPOINTS.CLIENT_BASE_URL}/clientActivities/${validateApiParamValue(id)}`,
      );
      return res.data;
    },
    retry: false,
  });
};

export const useGetClientActivitiesByClientId = () => {
  const [params, setParams] = useState<string>();
  const query = useQuery({
    queryKey: ["getClientActivities", params],
    queryFn: async () => {
      if (!params) {
        return [];
      }
      const res = await apiInstance.get(
        API_ENDPOINTS.getClientActivities(params ?? ""),
      );
      return res.data;
    },
    enabled: !!params, // Only run when params are set
    retry: false,
  });

  const refetchWithParams = (newParams: string) => {
    setParams(newParams);
  };
  return { ...query, refetchWithParams };
};
export const useDeleteCreateClientActivityMutation = () => {
  return useMutation<null, Error, any>({
    mutationFn: ({ activityId }: { activityId: string }) => {
      return apiInstance.delete(
        `${API_ENDPOINTS.CLIENT_BASE_URL}/clientActivities/${activityId}`,
      );
    },
  });
};

export const useGetClientDeliverablesByClientId = () => {
  const [params, setParams] = useState<string>();
  const query = useQuery({
    queryKey: ["getClientDeliverables", params],
    queryFn: async () => {
      if (!params) {
        return [];
      }
      const res = await apiInstance.get(
        API_ENDPOINTS.getClientDeliverables(params ?? ""),
      );
      return res.data;
    },
    enabled: !!params, // Only run when params are set
    retry: false,
  });

  const refetchWithParams = (newParams: string) => {
    setParams(newParams);
  };
  return { ...query, refetchWithParams };
};

export const useCreateClientActivityMutation = () => {
  return useMutation({
    mutationFn: ({
      payload,
      clientId,
    }: {
      payload: CreateClientActivityPayload;
      clientId: string;
    }) =>
      apiInstance.post(
        `${API_ENDPOINTS.CLIENT_BASE_URL}/clients/${validateApiParamValue(clientId)}/activities`,
        payload,
      ),
  });
};

export const useCreateClientActivityUpdateMutation = () => {
  return useMutation({
    mutationFn: ({ payload }: { payload: CreateClientActivityPayload }) => {
      return apiInstance.put(
        `${API_ENDPOINTS.CLIENT_BASE_URL}/clientActivities/${payload.id}`,
        payload,
      );
    },
  });
};

export const useGetCreateClientActivityDeliverables = (clientId: string) => {
  return useQuery({
    queryKey: ["getActivityDeliverables"],
    queryFn: async () => {
      clientId = clientId ?? "";
      const res = await apiInstance.get(
        `${API_ENDPOINTS.CLIENT_BASE_URL}/clients/${validateApiParamValue(clientId)}/deliverables`,
      );
      return res.data;
    },
    retry: false,
  });
};

export const useGetExistingClientDeliverables = () => {
  const [params, setParams] = useState<ExistingDeliverableParams>();
  const query = useQuery({
    queryKey: ["getExistingDeliverables"],
    queryFn: async () => {
      if (!params) {
        return [];
      }
      const res = await apiInstance.get(
        `${API_ENDPOINTS.MDM_BASE_URL}/deliverables/master?frameworkType=${validateApiParamValue(params.frameworkType)}`,
      );
      return res.data;
    },
    retry: false,
    enabled: !!params,
  });

  const refetchWithParams = (newParams: ExistingDeliverableParams) => {
    setParams((prevParams) => ({
      ...newParams,
      isRefetch: !prevParams?.isRefetch, // Flip the boolean value
    }));
  };

  return { ...query, refetchWithParams };
};

export const useAddNewDeliverableMutation = () => {
  return useMutation({
    mutationFn: ({
      params,
      clientId,
    }: {
      params: CreateDeliverableParams;
      clientId: string;
    }) => {
      return apiInstance.post(
        `${API_ENDPOINTS.CLIENT_BASE_URL}/clients/${validateApiParamValue(clientId)}/deliverables`,
        params,
      );
    },
  });
};

export const useUpdateClientDeliverableMutation = () => {
  return useMutation({
    mutationFn: ({
      params,
      deliverableId,
    }: {
      params: CreateDeliverableParams;
      deliverableId: string;
    }) => {
      return apiInstance.put(
        `${API_ENDPOINTS.CLIENT_BASE_URL}/clientDeliverables/${validateApiParamValue(deliverableId)}`,
        params,
      );
    },
  });
};

export const useAddExistingClientDeliverableMutation = () => {
  return useMutation({
    mutationFn: ({
      params,
      clientId,
    }: {
      params: AddExistingDeliverableParams;
      clientId: string;
    }) => {
      return apiInstance.post(
        `${API_ENDPOINTS.CLIENT_BASE_URL}/clients/${validateApiParamValue(clientId)}/cloneDeliverables`,
        params,
      );
    },
  });
};

export const useGetCreateClientDeliverableById = () => {
  const [deliverableId, setDeliverableId] = useState<string>("");

  const query = useQuery({
    queryKey: ["getCreateClientDeliverableById", deliverableId],
    queryFn: async () => {
      if (!deliverableId) {
        return [];
      }
      const res = await apiInstance.get(
        `${API_ENDPOINTS.CLIENT_BASE_URL}/clientDeliverables/${validateApiParamValue(deliverableId)}`,
      );
      return res.data;
    },
    enabled: !!deliverableId, // Only run when params are set
    retry: false,
  });

  const refetchWithParams = (newParams: string) => {
    setDeliverableId(newParams);
  };

  return { ...query, refetchWithParams };
};

export const useGetTopicList = () => {
  const [clientId, setClientId] = useState<string>("");
  const [programTemplateId, setProgramTemplateId] = useState<string>();
  const query = useQuery({
    queryKey: ["getTopicList", clientId, programTemplateId],
    queryFn: async (): Promise<IdNameList[]> => {
      if (!clientId || !programTemplateId) {
        return [];
      }
      const queryParams = new URLSearchParams();
      queryParams.append("clientId", clientId);
      queryParams.append("programTemplateId", programTemplateId);
      const res = await apiInstance.get(
        `${API_ENDPOINTS.GET_TOPIC_LIST}?${queryParams.toString()}`,
      );
      return res.data;
    },
    enabled: !!clientId && !!programTemplateId, // Only run when params are set
    retry: false,
  });

  const refetchWithParams = (clientId: string, programTemplateId: string) => {
    setClientId(clientId);
    setProgramTemplateId(programTemplateId);
  };

  const clearListOfTopic = () => {
    setClientId("");
    setProgramTemplateId("");
    // it will return [] array
  };
  return { ...query, refetchWithParams, clearListOfTopic };
};

export const useGetFeatureList = () => {
  const [params, setParams] = useState<FeatureListParams>();

  const query = useQuery({
    queryKey: ["getFeatureList", params],
    queryFn: async () => {
      if (
        !params ||
        isEmpty(params.countryId) ||
        isEmpty(params.programClassId)
      ) {
        return [];
      }
      const res = await apiInstance.get(API_ENDPOINTS.GET_FEATURE_LIST, {
        params,
      });
      return res.data;
    },
    // The query will not execute until the countryCodes exists
    enabled: !!params,
    retry: false,
  });
  const refetchWithParams = (newParams: FeatureListParams) => {
    setParams(newParams);
  };

  return { ...query, refetchWithParams };
};

export const useCreateGenericAccountMutation = () => {
  return useMutation({
    mutationFn: (params: CreateGenericAccountParams) => {
      return apiInstance.post(
        API_ENDPOINTS.CREATE_UPDATE_GENERIC_ACCOUNT,
        params,
      );
    },
  });
};

export const useUpdateGenericAccountMutation = () => {
  return useMutation({
    mutationFn: (params: UpdateGenericAccountParams) => {
      return apiInstance.put(
        `${API_ENDPOINTS.CREATE_UPDATE_GENERIC_ACCOUNT}/${params.genericAccountId}`,
        params,
      );
    },
  });
};

export const useGetGenericAccountByIdMutation = () => {
  return useMutation({
    mutationFn: async (genericAccountId: string) => {
      const res = await apiInstance.get(
        `${API_ENDPOINTS.getGenericAccountById(genericAccountId)}`,
      );
      return res.data as GetGenericAccountsResponse;
    },
  });
};

export const useSearchGenericAccountsMutation = () => {
  return useMutation({
    mutationFn: (params: SearchGenericAccountsParams) => {
      return apiInstance.post(`${API_ENDPOINTS.GET_GENERIC_ACCOUNTS}`, params);
    },
  });
};

export const useGetGenericAccountKeyDetailsMutation = () => {
  return useMutation({
    mutationFn: async (genericAccountId: string) => {
      const res = await apiInstance.get(
        `${API_ENDPOINTS.getGenericAccountKeyDetails(genericAccountId)}`,
      );
      return res.data as GenericAccountKeyDetails;
    },
  });
};

export const useGetGenericAccountParticipantByIdMutation = () => {
  return useMutation({
    mutationFn: async (participantId: string) => {
      const res = await apiInstance.get(
        API_ENDPOINTS.getUpdateGenericAccountParticipantById(participantId),
      );
      return res.data as GenericAccountParticipantResponse;
    },
  });
};

export const useUpdateParticipantsGenericAccountByIdMutation = () => {
  return useMutation({
    mutationFn: (params: UpdateParticipantDataParams) => {
      return apiInstance.put(
        API_ENDPOINTS.getUpdateGenericAccountParticipantById(
          params.participantId,
        ),
        params,
      );
    },
  });
};

export const useGetTimeZoneList = () => {
  const [params, setParams] = useState<string>();

  const query = useQuery({
    queryKey: ["getTimeZoneList", params],
    queryFn: async (): Promise<DropDownData[]> => {
      if (!params || isEmpty(params)) {
        return [];
      }
      const res = await apiInstance.get(
        API_ENDPOINTS.GET_TIMEZONE_LIST(params ?? ""),
      );
      return res.data && !!res.data[0] ? res.data[0].items : [];
    },
    // The query will not execute until the params exists
    enabled: !!params,
    retry: false,
  });
  const refetchWithParams = (languageCode: string) => {
    setParams(languageCode);
  };

  return { ...query, refetchWithParams };
};

export const useGetPurchaseOrdersByProgramTemplateId = () => {
  const [params, setParams] = useState<{
    clientId: string;
    programTemplateId: string;
  }>();

  const query = useQuery({
    queryKey: ["useGetPurchaseOrdersByProgramTemplateId", params],
    queryFn: async (): Promise<PurchaseOrder[]> => {
      if (!params?.clientId || !params) {
        return [];
      }

      const queryParams = new URLSearchParams();
      queryParams.append("programTemplateId", params.programTemplateId);
      let url = `${API_ENDPOINTS.getPurchaseOrder(params.clientId)}?${queryParams.toString()}`;
      const res = await apiInstance.get(url);

      return res.data;
    },
    enabled: !!params, // Only run when params are set
    retry: false,
  });

  const refetchWithParams = (clientId: string, programTemplateId: string) => {
    setParams({
      clientId,
      programTemplateId,
    });
  };

  return { ...query, refetchWithParams };
};

export const useGetPurchaseOrdersByClientWithActiveStatus = () => {
  const [params, setParams] = useState<{
    clientId: string;
    status: string;
  }>();

  const query = useQuery({
    queryKey: ["useGetPurchaseOrdersByClientWithActiveStatus", params],
    queryFn: async (): Promise<PurchaseOrder[]> => {
      if (!params?.clientId || !params?.status) {
        return [];
      }

      let url = `${API_ENDPOINTS.getPurchaseOrderByClientWithActiveStatus(params.clientId, params.status)}`;
      const res = await apiInstance.get(url);

      return res.data;
    },
    enabled: !!params, // Only run when params are set
    retry: false,
  });

  const refetchWithParams = (clientId: string, status: string) => {
    setParams({
      clientId,
      status,
    });
  };

  return { ...query, refetchWithParams };
};

export const useGetProgramExpenseById = () => {
  return useMutation({
    mutationFn: async (programExpenseId: string) => {
      const res = await apiInstance.get(
        API_ENDPOINTS.getProgramExpenseById(programExpenseId),
      );
      return res.data as ViewFeeOrExpenseDetails;
    },
  });
};

export const useGetInvoiceByIdMutation = () => {
  return useMutation({
    mutationFn: async (invoiceId: string) => {
      const res = await apiInstance.get(
        API_ENDPOINTS.getInvoiceById(invoiceId),
      );
      return res.data as ViewInvoiceResponse;
    },
  });
};

export const useGetInvoiceHistoryByProgramId = () => {
  const [params, setParams] = useState<string>();

  const query = useQuery({
    queryKey: ["GetInvoiceList", params],
    queryFn: async () => {
      if (!params) return null;
      const res = await apiInstance.get(
        API_ENDPOINTS.getInvoicesByProgramId(params ?? ""),
      );
      return res.data as ProgramInvoiceSummary;
    },
    enabled: !!params,
    retry: false,
  });

  const refetchWithParams = (programId: string) => {
    setParams(programId);
    query.refetch();
  };

  return { ...query, refetchWithParams };
};

export const useReverseInvoiceMutation = () => {
  return useMutation({
    mutationFn: async ({
      invoiceId,
      date,
      reason,
      comments,
    }: ReverseInvoiceParams) => {
      const res = await apiInstance.post(
        API_ENDPOINTS.getInvoiceById(invoiceId),
        {
          action: "reverse",
          date,
          reason,
          comments,
        },
      );
      return res.data;
    },
  });
};

export const useExportInvoiceHistoryToExcelMutation = () => {
  return useMutation<AxiosResponse<Blob>, Error, InvoiceHistoryRowExportParams>(
    {
      mutationFn: (
        params: InvoiceHistoryRowExportParams,
      ): Promise<AxiosResponse<Blob>> => {
        let url = API_ENDPOINTS.getInvoiceExportDataByProgramId(
          params.invoiceId,
        );
        if (params.type === "project") {
          url = API_ENDPOINTS.getInvoiceExportDataByProjectId(params.invoiceId);
        }
        return apiInstance.get(url, {
          responseType: "blob",
        });
      },
    },
  );
};

export const useCreateProgramInvoiceMutation = () => {
  return useMutation<
    { id: string; number: string },
    Error,
    { programId: string; payload: PurchaseOrderExpensePayload }
  >({
    mutationFn: ({
      programId,
      payload,
    }: {
      programId: string;
      payload: PurchaseOrderExpensePayload;
    }) => {
      return apiInstance
        .post(API_ENDPOINTS.createInvoiceByProgramId(programId), payload)
        .then((res) => res.data as { id: string; number: string });
    },
  });
};

export const useDeleteGenericAccountMutation = () => {
  return useMutation({
    mutationFn: (params: { genericAccountId: string }) => {
      return apiInstance.delete(
        `${API_ENDPOINTS.deleteGenericAccount(params.genericAccountId)}`,
      );
    },
  });
};

/**
 *
 * @useDeleteFeesAndExpense mutation hook to delete fees and expense by ID.
 */
export const useDeleteFeesAndExpense = () => {
  return useMutation({
    mutationFn: (feesAndExpenseId: string) => {
      return apiInstance.delete(
        `${API_ENDPOINTS.getProgramExpenseById(feesAndExpenseId)}`,
      );
    },
  });
};

export const useGetLanguages = () => {
  return useQuery<string[]>({
    queryKey: ["getLanguages"],
    queryFn: async (): Promise<string[]> => {
      const res = await apiInstance.get(API_ENDPOINTS.GET_LANGUAGES);
      return res.data;
    },
    retry: false,
  });
};
export const useGetIndustryListForDropdowns = () => {
  return useQuery({
    queryKey: ["getIndustryList"],
    queryFn: async (): Promise<DropDownData[]> => {
      const res = await apiInstance.get(
        API_ENDPOINTS.GET_INDUSTRY_DROPDOWN_LIST,
      );
      return res.data;
    },
    retry: false,
  });
};

export const useGetInvoicingLanguages = () => {
  const [params, setParams] = useState<GetInvoicingLanguagesParams>();
  const query = useQuery<InvoicingLanguage[]>({
    queryKey: ["getInvoicingLanguages", params],
    queryFn: async (): Promise<InvoicingLanguage[]> => {
      const res = await apiInstance.get(
        API_ENDPOINTS.getInvoicingLanguages(
          params?.clientId,
          params?.programId,
        ),
      );
      return res.data;
    },
    retry: false,
  });

  const refetchWithParams = (newParams: GetInvoicingLanguagesParams) => {
    setParams(newParams);
  };

  return { ...query, refetchWithParams };
};

export const useGetClientList = () => {
  const [params, setParams] = useState<GetClientListParams>();

  const query = useQuery({
    queryKey: ["getClientList", params],
    queryFn: async (): Promise<GetClientListResponse[]> => {
      if (!params) {
        return [];
      }
      const {
        keyword = "",
        isActiveClientOnly = false,
        excludeNCBPClient = false,
        clientType,
      } = params;
      const res = await apiInstance.get(
        API_ENDPOINTS.GET_CLIENT_LIST_AUTOCOMPLETE(
          keyword,
          isActiveClientOnly,
          excludeNCBPClient,
          clientType,
        ),
      );
      return res.data;
    },
    enabled: !!params, // Only run when params are set
    retry: false,
  });

  const refetchWithParams = (newParams: GetClientListParams) => {
    setParams((prevParams) => ({
      ...newParams,
      isRefetch: !prevParams?.isRefetch, // Flip the boolean value
    }));
  };

  return { ...query, refetchWithParams };
};

export const useValidateOpportunityIdMutation = () => {
  return useMutation({
    mutationFn: async (
      validateOpportunityPayload: ValidateOpportunityPayload,
    ): Promise<IdNameList> => {
      const res = await apiInstance.post(
        `${API_ENDPOINTS.VALIDATE_OPPORTUNITY_ID}`,
        {
          ...validateOpportunityPayload,
        },
      );
      return res.data;
    },
  });
};

export const useGetContractDocumentDropdownPreload = (
  paramsList: GetContractDocumentPayload[],
) => {
  const queries = useQueries({
    queries: paramsList.map((params) => ({
      queryKey: ["useGetContractDocumentDropdown", params],
      queryFn: async (): Promise<GetContractDocumentResponse[]> => {
        return (
          await apiInstance.get<GetContractDocumentResponse[]>(
            API_ENDPOINTS.getContractDocumentDropdown(params),
          )
        ).data;
      },
      enabled: Boolean(params),
      retry: false,
    })),
  });

  return {
    queries, // <-- array, one item per param
  };
};

export const useGetClientSalesOfficesList = () => {
  const [clientId, setClientId] = useState<string>();

  const query = useQuery({
    queryKey: ["getClientSalesOfficesList", clientId],
    queryFn: async (): Promise<GetClientSalesOfficesListResponse[]> => {
      if (!clientId) {
        return [];
      }
      const res = await apiInstance.get(
        API_ENDPOINTS.getClientSalesOffices(clientId),
      );

      return res.data;
    },
    enabled: !!clientId, // Only run when clientId is set
    retry: false,
  });

  const refetchWithParams = (newClientId: string) => {
    setClientId(newClientId);
  };

  return { ...query, refetchWithParams };
};
